/**
 * Angular service for the Featured Post API.
 *
 * @remarks
 * Manages an organisation's single featured blog post with:
 * - Reactive signal-based state (`featuredPost`, `loading`, `error`)
 * - A public GET endpoint consumed without authentication
 * - Authenticated PATCH endpoints for featuring / unfeaturing a post
 *   (auth handled by the app-level `authInterceptor`, not by this service)
 *
 * Only one post can be featured per organisation at a time. The backend
 * atomically demotes any previous featured post when a new one is set —
 * do NOT call `unfeaturePost()` before `featurePost()`.
 *
 * @example
 * ```typescript
 * // Read the featured post in a public component
 * readonly featuredSvc = inject(FeaturedPostService);
 *
 * ngOnInit() {
 *   this.featuredSvc.getFeaturedPost(this.orgId).subscribe();
 * }
 * // Template: featuredSvc.featuredPost()
 *
 * // Author action — set a post as featured
 * this.featuredSvc.featurePost(post._id).subscribe({
 *   error: () => alert(this.featuredSvc.error())
 * });
 * ```
 *
 * @see FEATURED_POST_ANGULAR_SERVICE.md for full API reference
 */

import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, tap, throwError } from 'rxjs';

import { CMS_POST_API_CONFIG } from '../config/blog-post-config';
import {
  FeatureActionResponse,
  FeaturedBlogPost,
  FeaturedPostResponse,
} from '../models/blog-post.model';

// ---------------------------------------------------------------------------
// State shape (internal)
// ---------------------------------------------------------------------------

interface FeaturedPostState {
  featuredPost: FeaturedBlogPost | null;
  loading: boolean;
  error: string | null;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

/**
 * Service for reading and managing an organisation's featured blog post.
 *
 * Holds signal-based reactive state so consuming components read signals
 * directly without manual change detection.
 */
@Injectable({ providedIn: 'root' })
export class FeaturedPostService {
  private readonly _http = inject(HttpClient);
  private readonly _config = inject(CMS_POST_API_CONFIG);
  private readonly _baseUrl = `${this._config.apiBaseUrl}/api/blog-posts`;

  // -------------------------------------------------------------------------
  // Reactive state
  // -------------------------------------------------------------------------

  private readonly _state = signal<FeaturedPostState>({
    featuredPost: null,
    loading: false,
    error: null,
  });

  /** The currently featured post for the loaded organisation, or `null`. */
  readonly featuredPost = computed(() => this._state().featuredPost);

  /** `true` while any HTTP request initiated by this service is in flight. */
  readonly loading = computed(() => this._state().loading);

  /** Error message from the most recent failed request, or `null`. */
  readonly error = computed(() => this._state().error);

  /** Convenience: `true` when a featured post is loaded. */
  readonly hasFeaturedPost = computed(() => this._state().featuredPost !== null);

  // -------------------------------------------------------------------------
  // Public GET — no authentication required
  // -------------------------------------------------------------------------

  /**
   * Fetch the featured post for an organisation.
   *
   * This is a **public** endpoint — no `Authorization` header is sent.
   * The response is cached on the server for 15 minutes (`Cache-Control`).
   * On success the `featuredPost` signal is updated; `null` means the
   * organisation has no featured post (not an error condition).
   *
   * @param orgId - MongoDB ObjectId of the organisation
   */
  getFeaturedPost(orgId: string): Observable<FeaturedPostResponse> {
    this._patchState({ loading: true, error: null });
    this._log('getFeaturedPost', { orgId });

    return this._http
      .get<FeaturedPostResponse>(`${this._baseUrl}/public/featured`, {
        params: { orgId },
      })
      .pipe(
        tap(({ data }) => {
          this._patchState({ featuredPost: data, loading: false });
          this._log('getFeaturedPost — success', data ? data._id : 'null');
        }),
        catchError((err: unknown) => {
          const message = this._extractMessage(err, 'Failed to load featured post');
          this._patchState({ loading: false, error: message });
          this._log('getFeaturedPost — error', message);
          return throwError(() => err);
        }),
      );
  }

  // -------------------------------------------------------------------------
  // Authenticated write operations
  // -------------------------------------------------------------------------

  /**
   * Set a blog post as the featured post for its organisation.
   *
   * **Auth required** — the `authInterceptor` must attach a valid Firebase
   * Bearer token. Only the post's author may call this (HTTP 403 otherwise).
   *
   * The backend atomically demotes any previously featured post in the org.
   * Do **not** call `unfeaturePost()` first.
   *
   * On success the `featuredPost` signal is updated with the API response,
   * avoiding a redundant re-fetch.
   *
   * @param postId - MongoDB ObjectId of the blog post to feature
   */
  featurePost(postId: string): Observable<FeatureActionResponse> {
    this._patchState({ loading: true, error: null });
    this._log('featurePost', { postId });

    return this._http
      .patch<FeatureActionResponse>(`${this._baseUrl}/${postId}/feature`, {})
      .pipe(
        tap(({ data }) => {
          this._patchState({ featuredPost: data, loading: false });
          this._log('featurePost — success', postId);
        }),
        catchError((err: unknown) => {
          const message = this._extractMessage(err, 'Failed to set featured post');
          this._patchState({ loading: false, error: message });
          this._log('featurePost — error', message);
          return throwError(() => err);
        }),
      );
  }

  /**
   * Remove the featured status from a blog post.
   *
   * **Auth required** — only the post's author may call this.
   * On success the `featuredPost` signal is cleared to `null`.
   *
   * @param postId - MongoDB ObjectId of the blog post to unfeature
   */
  unfeaturePost(postId: string): Observable<FeatureActionResponse> {
    this._patchState({ loading: true, error: null });
    this._log('unfeaturePost', { postId });

    return this._http
      .patch<FeatureActionResponse>(`${this._baseUrl}/${postId}/unfeature`, {})
      .pipe(
        tap(() => {
          this._patchState({ featuredPost: null, loading: false });
          this._log('unfeaturePost — success', postId);
        }),
        catchError((err: unknown) => {
          const message = this._extractMessage(err, 'Failed to remove featured status');
          this._patchState({ loading: false, error: message });
          this._log('unfeaturePost — error', message);
          return throwError(() => err);
        }),
      );
  }

  // -------------------------------------------------------------------------
  // State management
  // -------------------------------------------------------------------------

  /**
   * Reset all state to initial values.
   *
   * Call this on user logout or when switching organisations to prevent
   * stale featured-post data from appearing for a different session.
   */
  reset(): void {
    this._patchState({ featuredPost: null, loading: false, error: null });
    this._log('state reset');
  }

  // -------------------------------------------------------------------------
  // Private helpers
  // -------------------------------------------------------------------------

  private _patchState(patch: Partial<FeaturedPostState>): void {
    this._state.update((s) => ({ ...s, ...patch }));
  }

  /**
   * Extracts a human-readable error message from an unknown thrown value.
   *
   * Priority:
   * 1. `HttpErrorResponse.error.message` — API error body (most descriptive)
   * 2. `HttpErrorResponse.message` — generic HTTP description
   * 3. `Error.message` — plain JS error
   * 4. `fallback` — hardcoded string
   */
  private _extractMessage(err: unknown, fallback: string): string {
    if (typeof err === 'object' && err !== null) {
      const httpErr = err as Record<string, unknown>;
      if (typeof httpErr['error'] === 'object' && httpErr['error'] !== null) {
        const apiErr = httpErr['error'] as Record<string, unknown>;
        if (typeof apiErr['message'] === 'string') return apiErr['message'];
      }
      if (typeof httpErr['message'] === 'string') return httpErr['message'];
    }
    if (err instanceof Error) return err.message;
    return fallback;
  }

  private _log(message: string, ...args: unknown[]): void {
    if (this._config.debug) {
      console.log(`[FeaturedPostService] ${message}`, ...args);
    }
  }
}
